import { ComponentConfig } from '@measured/puck';

import { Header as HeaderComponent, HeaderProps } from '../../../../../components/atoms/header/header';
import { getIconComponent, getIconNames, IconName } from '../../../../../components/atoms/icon/icon-utils';
import { Link } from '../../../../../components/atoms/link/link';
import { useReportContext } from '../../../renderer/report-context';
import * as contentUtils from '../../../utils/content';
import * as linkUtils from '../../../utils/links';

type HeaderBlockProps = {
  readonly anchor?: string;
  readonly text: string;
  readonly level: HeaderProps['level'];
  readonly link?: string;
  readonly externalLink?: string;
  readonly icon?: IconName;
};

const iconNames = getIconNames();

const Header = ({ level, text, link, externalLink, anchor, icon }: HeaderBlockProps) => {
  const { navigateToPath } = useReportContext();
  const header = (
    <HeaderComponent id={anchor} level={level}>
      {text}
    </HeaderComponent>
  );

  const navigateTo = () => {
    if (externalLink) return navigateToPath(externalLink);

    return link && navigateToPath(link);
  };

  if (link || externalLink) {
    const IconComponent = icon ? getIconComponent(icon) : null;
    return (
      <Link
        className="flex flex-row items-center underline text-typography w-fit"
        data-testid="header-link"
        onClick={navigateTo}
        onKeyDown={(e) => e.key === 'Enter' && navigateTo()}
      >
        {header}
        {IconComponent && <IconComponent className="inline-block ml-1" size={16} />}
      </Link>
    );
  }

  return header;
};

const createHeaderBlock = (): ComponentConfig<HeaderBlockProps> => ({
  defaultProps: {
    text: 'This is a heading',
    level: 'h1',
  },
  fields: {
    text: {
      type: 'text',
    },
    anchor: {
      type: 'text',
    },
    level: {
      type: 'select',
      options: [
        { label: 'H1', value: 'h1' },
        { label: 'H2', value: 'h2' },
        { label: 'H3', value: 'h3' },
        { label: 'H4', value: 'h4' },
        { label: 'H5', value: 'h5' },
      ],
    },
  },
  label: 'Header',
  render: Header,
  resolveFields: ({ props }, { fields, appState }) => {
    const allLinks = contentUtils.getAllAnchors(appState.data.content);
    return {
      ...fields,
      ...linkUtils.createLinkFields(allLinks, props.link === linkUtils.EXTERNAL_OPTION_VALUE),
      ...(props.link && {
        icon: {
          label: 'Icon',
          type: 'select',
          options: [{ label: 'Select icon...', value: '' }].concat(
            iconNames.map((iconName) => ({
              label: iconName,
              value: iconName,
            }))
          ),
        },
      }),
    };
  },
});

export { createHeaderBlock };
